import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatNOK } from "@/lib/format";
import { amountToPct, pctToAmount, validateInterimAmount } from "@/lib/invoice-plan";

interface AdHocInterimDialogProps {
  contractAmount: number;
  /** Det som gjenstår av kontrakten (eks. ufakturert mersalg). */
  remaining: number;
  onCancel: () => void;
  onContinue: (amount: number, pct: number) => void;
}

/**
 * VIB-2836: ad hoc-mellomfaktura utenfor planen. Beløpet kan settes i kroner
 * eller som prosent av kontrakten — feltene oppdaterer hverandre. Selve
 * fakturaen lages i opprett-faktura-dialogen etterpå.
 */
export function AdHocInterimDialog({
  contractAmount,
  remaining,
  onCancel,
  onContinue,
}: AdHocInterimDialogProps) {
  const [amountInput, setAmountInput] = useState("");
  const [pctInput, setPctInput] = useState("");

  const amount = Number(amountInput);
  const pct = Number(pctInput);
  const error = amountInput === "" ? null : validateInterimAmount(amount, remaining);
  const canContinue = amountInput !== "" && error == null;

  const onAmountChange = (value: string) => {
    setAmountInput(value);
    setPctInput(value === "" ? "" : String(amountToPct(Number(value), contractAmount)));
  };

  const onPctChange = (value: string) => {
    setPctInput(value);
    setAmountInput(value === "" ? "" : String(pctToAmount(Number(value), contractAmount)));
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ny mellomfaktura</DialogTitle>
          <DialogDescription>
            Mellomfakturaen legges inn i faktureringsplanen, og sluttfakturaen blir det som gjenstår.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="adhoc-interim-amount" className="text-xs text-muted-foreground">
              Beløp eks. mva (kr)
            </Label>
            <Input
              id="adhoc-interim-amount"
              type="number"
              min={0}
              inputMode="decimal"
              value={amountInput}
              onChange={(e) => onAmountChange(e.target.value)}
              className="text-right tabular-nums"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="adhoc-interim-pct" className="text-xs text-muted-foreground">
              Andel av kontrakten (%)
            </Label>
            <Input
              id="adhoc-interim-pct"
              type="number"
              min={0}
              max={100}
              step={0.01}
              inputMode="decimal"
              value={pctInput}
              onChange={(e) => onPctChange(e.target.value)}
              className="text-right tabular-nums"
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Gjenstår av kontrakten: {formatNOK(remaining)} ({amountToPct(remaining, contractAmount)} %)
        </p>
        {error && <p className="text-xs text-destructive" role="alert">{error} Gjenstår: {formatNOK(remaining)}.</p>}

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Avbryt
          </Button>
          <Button size="sm" disabled={!canContinue} onClick={() => onContinue(amount, pct)}>
            Fortsett
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
